import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Role rank mirrors backend/rbac.py — keep in sync.
const ROLE_RANK = {
  periodical_viewer: 1,
  periodical_admin: 2,
  pub_viewer: 3,
  pub_admin: 4,
  org_viewer: 5,
  org_admin: 6,
  superadmin: 7,
};
const WRITE_ROLES = new Set(["periodical_admin", "pub_admin", "org_admin", "superadmin"]);

export function AuthProvider({ children }) {
  // null = checking, false = unauth, object = user
  const [user, setUser] = useState(null);
  const [viewAsId, setViewAsId] = useState(() => localStorage.getItem("view_as_id") || null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) localStorage.setItem("access_token", data.access_token);
    // Pull the fresh profile so we have scope_summary
    const full = await refresh();
    return full || data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("access_token");
    localStorage.removeItem("view_as_id");
    setViewAsId(null);
    setUser(false);
  };

  // -------- View-as (SuperAdmin sudo, read-only) --------
  const enterViewAs = async (targetUserId) => {
    localStorage.setItem("view_as_id", targetUserId);
    setViewAsId(targetUserId);
    await refresh();
  };
  const exitViewAs = async () => {
    localStorage.removeItem("view_as_id");
    setViewAsId(null);
    await refresh();
  };

  // -------- Role helpers --------
  const role = user && typeof user === "object" ? user.role : null;
  const isViewAsActive = !!user?.ctx?.view_as_active;
  const canWrite = role ? (WRITE_ROLES.has(role) && !isViewAsActive) : false;
  const isSuperAdmin = role === "superadmin" && !isViewAsActive;

  const hasRole = useCallback((min) => {
    if (!role) return false;
    return (ROLE_RANK[role] || 0) >= (ROLE_RANK[min] || 999);
  }, [role]);

  return (
    <AuthContext.Provider value={{
      user, setUser, login, logout, refresh,
      role, canWrite, isSuperAdmin, isViewAsActive,
      viewAsId, enterViewAs, exitViewAs,
      hasRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
